'use client'

import { FC, ReactNode, Suspense } from 'react'

import { UserProvider } from '@/widgets/user'

import { YaPayProvider } from '@/features/YaSplit'

import { AiChatProvider } from '@/entities/ai-chat'
import {
	getIsRegion,
	getRegionBaseUrl,
	RegionContextProvider,
	SPB_CITY_CODE,
	SPB_CITY_NAME,
	TRegion
} from '@/entities/regions'

import { WebPushProvider } from '@/shared/integrations/web-push'
import { DebugProvider } from '@/shared/lib/debug'
import { SearchBotContextProvider } from '@/shared/lib/seo'

import { Analytics } from './Analytics'
import { ErrorBoundary } from './error-boundary'
import { syncApiRegion } from './lib/syncApiRegion'
import { ThemeProvider } from './ThemeProvider/ThemeProvider'

interface ProvidersProps {
	children: ReactNode
	allRegions: TRegion[]
	currentRegion: TRegion | null
	isSearchBot?: boolean
}

export const Providers: FC<ProvidersProps> = ({ children, allRegions, currentRegion, isSearchBot = false }) => {
	const regionValue = currentRegion || {
		REGION_CODE: SPB_CITY_CODE,
		REGION_NAME: SPB_CITY_NAME,
		REGION_P_NAME: '',
		REGION_PHONE: ''
	}

	// Legacy parity: keep global API.region in sync with SSR-selected region,
	// so client-side requests without CITY_* cookies still use correct region.
	if (typeof window !== 'undefined') {
		syncApiRegion(regionValue)
	}

	return (
		<WebPushProvider>
			<ErrorBoundary>
				<ThemeProvider>
					<DebugProvider>
						<SearchBotContextProvider isBot={isSearchBot}>
							<RegionContextProvider
								value={{
									currentRegion: regionValue,
									allRegions,
									baseUrl: getRegionBaseUrl(currentRegion?.REGION_CODE),
									isRegion: getIsRegion(currentRegion?.REGION_CODE)
								}}>
								<UserProvider>
									<YaPayProvider>
										<AiChatProvider>
											{/*<Script id="experemental-init-script">{getMetricsScriptString()}</Script>*/}
											<Suspense>
												<Analytics />
											</Suspense>
											{children}
										</AiChatProvider>
									</YaPayProvider>
								</UserProvider>
							</RegionContextProvider>
						</SearchBotContextProvider>
					</DebugProvider>
				</ThemeProvider>
			</ErrorBoundary>
		</WebPushProvider>
	)
}
